import React, { useState, useEffect } from 'react';
import { useApi } from '../hooks/useApi';
import { useUser } from '../hooks/useUser';
import { sharedStyles } from '../styles/sharedStyles';
import { getWorkDate } from '../utils/timezone';
import { formatMachineOptionLabel, formatMachineOptionLabelNoFinance, getTicketLineAmount } from '../utils/ticketLineAmount';
import { hideFinancialUi } from '../utils/foremanUi';

const DuplicateTicketModal = ({ 
  show, 
  onClose, 
  originalTicket, 
  onDuplicate, 
  customers, 
  jobs, 
  employees 
}) => {
  const api = useApi();
  const { user } = useUser();
  const hideMoney = hideFinancialUi(user?.role);
  const [formData, setFormData] = useState({
    customerId: '',
    jobId: '',
    employeeId: '',
    workDate: '',
    description: '',
    notes: '',
    ticketItems: []
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [machines, setMachines] = useState([]);

  useEffect(() => {
    if (originalTicket && show) {
      setFormData({
        customerId: originalTicket.customerId || '',
        jobId: originalTicket.jobId || '',
        employeeId: originalTicket.employeeId || '',
        workDate: getWorkDate(),
        description: originalTicket.description || '',
        notes: originalTicket.notes || '',
        ticketItems: originalTicket.TicketItems ? originalTicket.TicketItems.map(item => ({
          ...item,
          employeeId: item.employeeId || '',
          rateType: item.rateType || 'hourly',
          hoursUsed: item.rateType === 'hourly' ? item.hoursUsed : '',
          loadsUsed: item.rateType === 'load' ? item.loadsUsed : '',
        })) : []
      });
      setError('');
    }
  }, [originalTicket, show]);

  useEffect(() => {
    // Load machines data
    const loadMachines = async () => {
      try {
        const machinesRes = await api({ method: 'get', url: '/machines?limit=1000' });
        if (machinesRes.data.success) setMachines(machinesRes.data.data.machines);
      } catch (err) {
        console.error('Error loading machines:', err);
      }
    };
    if (show) {
      loadMachines();
    }
  }, [api, show]);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleTicketItemChange = (index, field, value) => {
    setFormData(prev => {
      const items = [...prev.ticketItems];
      items[index] = { ...items[index], [field]: value };
      if (field === 'rateType') {
        if (value !== 'hourly') items[index].hoursUsed = '';
        if (value !== 'load') items[index].loadsUsed = '';
      }
      return { ...prev, ticketItems: items };
    });
  };

  const addTicketItem = () => {
    setFormData(prev => ({
      ...prev,
      ticketItems: [
        ...prev.ticketItems,
        { machineId: '', employeeId: '', rateType: 'hourly', hoursUsed: '', loadsUsed: '', description: '', notes: '' }
      ]
    }));
  };

  const removeTicketItem = (index) => {
    setFormData(prev => ({
      ...prev,
      ticketItems: prev.ticketItems.length > 1 ? prev.ticketItems.filter((_, i) => i !== index) : prev.ticketItems
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Validate
    if (!formData.customerId || !formData.jobId || !formData.workDate) {
      setError('Please fill in all required fields.');
      setLoading(false);
      return;
    }

    const validItems = formData.ticketItems.filter(item => 
      item.machineId && 
      item.employeeId && 
      (item.rateType === 'daily' || (item.rateType === 'load' && item.loadsUsed) || (item.rateType === 'hourly' && item.hoursUsed))
    );
    
    if (validItems.length === 0) {
      setError('Please add at least one machine with operator and the required rate details.');
      setLoading(false);
      return;
    }

    const cleanedItems = validItems.map(item => ({
      machineId: item.machineId,
      employeeId: item.employeeId,
      rateType: item.rateType,
      hoursUsed: item.rateType === 'hourly' ? item.hoursUsed : null,
      loadsUsed: item.rateType === 'load' ? item.loadsUsed : null,
      description: item.description,
      notes: item.notes
    }));

    try {
      await onDuplicate(originalTicket.id, {
        ...formData,
        ticketItems: cleanedItems
      });
      onClose();
    } catch (err) {
      setError(err.message || 'Error duplicating ticket');
    } finally {
      setLoading(false);
    }
  };

  const getCustomerJobs = (customerId) => {
    if (!jobs || jobs.length === 0) {
      return [];
    }
    
    // If no customer is selected, show all jobs
    if (!customerId) {
      return jobs;
    }
    
    // Filter jobs by customer
    return jobs.filter(job => parseInt(job.customerId) === parseInt(customerId));
  };

  if (!show || !originalTicket) return null;

  return (
    <>
      <div 
        className="modal-backdrop fade show" 
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 1400
        }}
        onClick={onClose}
      ></div>
      <div 
        className="modal fade show" 
        style={{ 
          display: 'flex',
          zIndex: 1500,
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '1rem'
        }} 
        tabIndex="-1"
      >
        {/* Responsive modal dialog - full width on mobile, centered on larger screens */}
        <div className="modal-dialog modal-dialog-scrollable modal-dialog-centered modal-lg" 
             style={{ 
               maxWidth: '800px', 
               width: '100%',
               margin: '0 auto',
               maxHeight: '80vh'
             }}>
          <div className="modal-content" style={{ 
            borderRadius: '16px', 
            border: 'none', 
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)', 
            maxHeight: '80vh',
            display: 'flex',
            flexDirection: 'column'
          }}>
            {/* Header */}
            <div className="modal-header border-0 pb-0" style={{ flexShrink: 0 }}>
              <h5 style={{
                fontSize: '1.25rem',
                fontWeight: '700',
                color: '#1a1f36',
                margin: 0,
                wordBreak: 'break-word'
              }}>
                <i className="bi bi-files me-2 text-primary"></i>
                Duplicate Ticket
              </h5>
              <button 
                type="button" 
                className="btn-close"
                onClick={onClose}
                aria-label="Close"
                style={{ fontSize: '1.25rem' }}
              ></button>
            </div>
            
            {/* Modal Body - Scrollable */}
            <div className="modal-body" style={{ 
              padding: '1.5rem', 
              overflowY: 'auto',
              flex: 1,
              minHeight: 0
            }}>
              <div className="alert alert-info mb-4" style={{ 
                borderRadius: '12px', 
                border: '1px solid #bae6fd',
                backgroundColor: '#f0f9ff',
                color: '#0369a1'
              }}>
                <i className="bi bi-info-circle me-2"></i>
                <strong>Original Ticket:</strong> {originalTicket.ticketNumber} - {originalTicket.Customer?.companyName} - {originalTicket.Job?.title}
              </div>

              <form onSubmit={handleSubmit}>
                {/* Basic Information */}
                <div className="row g-3 mb-3">
                  <div className="col-12 col-lg-6">
                    <label className="form-label">Customer *</label>
                    <select className="form-select" name="customerId" value={formData.customerId} onChange={handleFormChange} required>
                      <option value="">Select Customer</option>
                      {[...customers].sort((a, b) => (a.companyName || '').localeCompare(b.companyName || '', undefined, { sensitivity: 'base' })).map(customer => (
                        <option key={customer.id} value={customer.id}>{customer.companyName}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-12 col-lg-6">
                    <label className="form-label">Job Name *</label>
                    <select className="form-select" name="jobId" value={formData.jobId} onChange={handleFormChange} required>
                      <option value="">Select Job Name</option>
                      {getCustomerJobs(formData.customerId).map(job => (
                        <option key={job.id} value={job.id}>{job.title}</option>
                      ))}
                    </select>
                    {jobs.length === 0 && (
                      <small className="text-danger">
                        No jobs available. Please create jobs first.
                      </small>
                    )}
                  </div>
                </div>
                
                <div className="row g-3 mb-3">
                  <div className="col-12 col-lg-6">
                    <label className="form-label">Work Date *</label>
                    <input type="date" className="form-control" name="workDate" value={formData.workDate} onChange={handleFormChange} required />
                  </div>
                </div>
                <div className="row g-3 mb-3">
                  <div className="col-12 col-lg-6">
                    <label className="form-label">Description</label>
                    <textarea className="form-control" name="description" value={formData.description} onChange={handleFormChange} rows="2" placeholder="Brief description of work performed..." style={{ minHeight: '60px', maxHeight: '120px' }} />
                  </div>
                  <div className="col-12 col-lg-6">
                    <label className="form-label">Notes</label>
                    <textarea className="form-control" name="notes" value={formData.notes} onChange={handleFormChange} rows="2" placeholder="Additional notes..." style={{ minHeight: '60px', maxHeight: '120px' }} />
                  </div>
                </div>

                {!hideMoney &&
                  (() => {
                    const estimatedTotal = formData.ticketItems.reduce(
                      (sum, item) => sum + getTicketLineAmount(item, machines),
                      0
                    );
                    return estimatedTotal > 0 ? (
                      <div className="alert alert-secondary mb-3 d-flex justify-content-between align-items-center">
                        <strong>ESTIMATED TICKET TOTAL:</strong>
                        <span className="badge bg-dark fs-6">${estimatedTotal.toFixed(2)}</span>
                      </div>
                    ) : null;
                  })()}
                
                <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
                  <h6 className="mb-0">Equipment & Hours</h6>
                  <button type="button" className="btn btn-sm btn-outline-primary" onClick={addTicketItem}>
                    <i className="bi bi-plus-circle me-1"></i> Add Machine
                  </button>
                </div>
                {formData.ticketItems.map((item, index) => (
                  <div key={index} className="card mb-2 ticket-item-card">
                    <div className="card-body">
                      <div className="row g-2">
                        <div className="col-12 col-md-6 col-lg-3">
                          <label className="form-label">Machine *</label>
                          <select className="form-select" value={item.machineId} onChange={e => handleTicketItemChange(index, 'machineId', e.target.value)} required>
                            <option value="">Select Machine</option>
                            {machines
                              .sort((a, b) => a.name.localeCompare(b.name))
                              .map(machine => (
                                <option key={machine.id} value={machine.id}>
                                  {hideMoney
                                    ? formatMachineOptionLabelNoFinance(machine, String(formData.workDate || '').slice(0, 10))
                                    : formatMachineOptionLabel(machine, String(formData.workDate || '').slice(0, 10))}
                                </option>
                              ))}
                          </select>
                        </div>
                        
                        <div className="col-12 col-md-6 col-lg-3">
                          <label className="form-label">Operator *</label>
                          <select className="form-select" value={item.employeeId} onChange={e => handleTicketItemChange(index, 'employeeId', e.target.value)} required>
                            <option value="">Select Operator</option>
                            {employees.map(employee => (
                              <option key={employee.id} value={employee.id}>
                                {employee.name} - {employee.position}
                              </option>
                            ))}
                          </select>
                        </div>
                        
                        <div className="col-12 col-md-6 col-lg-2">
                          <label className="form-label">Rate Type *</label>
                          <select className="form-select" value={item.rateType} onChange={e => handleTicketItemChange(index, 'rateType', e.target.value)} required>
                            <option value="hourly">Hourly</option>
                            <option value="daily">Daily</option>
                            <option value="load">Per Load</option>
                          </select>
                        </div>
                        {item.rateType === 'hourly' && (
                          <div className="col-12 col-md-6 col-lg-2">
                            <label className="form-label">Hours *</label>
                            <input type="number" className="form-control" value={item.hoursUsed} onChange={e => handleTicketItemChange(index, 'hoursUsed', e.target.value)} step="0.5" min="0" required />
                          </div>
                        )}
                        {item.rateType === 'load' && (
                          <div className="col-12 col-md-6 col-lg-2">
                            <label className="form-label">Loads Hauled *</label>
                            <input type="number" className="form-control" value={item.loadsUsed} onChange={e => handleTicketItemChange(index, 'loadsUsed', e.target.value)} step="1" min="0" required />
                          </div>
                        )}
                        
                        <div className="col-12 col-md-6 col-lg-2">
                          <button type="button" className="btn btn-outline-danger btn-sm w-100" onClick={() => removeTicketItem(index)} disabled={formData.ticketItems.length === 1}>
                            <i className="bi bi-trash"></i>
                          </button>
                        </div>
                      </div>
                      {!hideMoney && item.machineId ? (
                        <div className="small text-muted mt-1">
                          Line amount:{' '}
                          <strong className="text-body">
                            ${getTicketLineAmount(item, machines).toFixed(2)}
                          </strong>
                          {item.rateType === 'daily' && (
                            <span> (daily rate × 1 day)</span>
                          )}
                          {item.rateType === 'load' && (
                            <span> (loads hauled × rate)</span>
                          )}
                        </div>
                      ) : null}
                      <div className="row g-2 mt-2">
                        <div className="col-12 col-lg-6">
                          <label className="form-label">Description</label>
                          <input type="text" className="form-control" value={item.description} onChange={e => handleTicketItemChange(index, 'description', e.target.value)} placeholder="Work description for this machine..." />
                        </div>
                        <div className="col-12 col-lg-6">
                          <label className="form-label">Notes</label>
                          <input type="text" className="form-control" value={item.notes} onChange={e => handleTicketItemChange(index, 'notes', e.target.value)} placeholder="Additional notes..." />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}

                {error && (
                  <div className="alert alert-danger" role="alert" style={{ 
                    borderRadius: '12px', 
                    border: '1px solid #fecaca',
                    backgroundColor: '#fef2f2',
                    color: '#dc2626'
                  }}>
                    <i className="bi bi-exclamation-triangle me-2"></i>
                    {error}
                  </div>
                )}

                {/* Footer */}
                <div className="modal-footer" style={{ 
                  borderTop: '1px solid #e5e9f0',
                  padding: '1rem 1.5rem',
                  backgroundColor: '#f8fafc',
                  flexShrink: 0
                }}>
                  <div className="d-flex gap-2 w-100 flex-wrap">
                    <button 
                      type="button" 
                      className="btn btn-secondary flex-fill"
                      onClick={onClose}
                      disabled={loading}
                      style={{
                        backgroundColor: '#ffffff',
                        color: '#1a1f36',
                        border: '1px solid #e5e9f0',
                        borderRadius: '8px',
                        padding: '0.625rem 1.25rem',
                        fontWeight: '600',
                        minHeight: '44px'
                      }}
                    >
                      Cancel
                    </button>
                    <button 
                      type="submit" 
                      className="btn btn-primary flex-fill"
                      disabled={loading}
                      style={{
                        backgroundColor: '#911311',
                        color: '#ffffff',
                        border: 'none',
                        borderRadius: '8px',
                        padding: '0.625rem 1.25rem',
                        fontWeight: '600',
                        minHeight: '44px'
                      }}
                    >
                      {loading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          <span className="d-none d-sm-inline">Duplicating...</span>
                          <span className="d-sm-none">Processing...</span>
                        </>
                      ) : (
                        <>
                          <i className="bi bi-files me-2"></i>
                          <span className="d-none d-sm-inline">Duplicate Ticket</span>
                          <span className="d-sm-none">Duplicate</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default DuplicateTicketModal; 